/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
import { type OnInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
    type BaseRequestOptionsArgs,
    type ColumnDef,
    type Rest,
    type TableConfig,
    DataTableComponent,
    LazyloadService,
    UNPLACEHOLDER,
} from '@dm/core';
import { NEW_DETAIL_ERROR_NAME } from 'app/common/constants';
import { SWITCH } from 'app/config';
import { type ErrorData, type StepData, Status, TaskCenterService, TaskData } from 'app/feature/monitor/task/task-center.service';

import { TaskBatchStrService } from './task-batch-str.service';

@Component({
  selector: 'app-om-task-batch',
  templateUrl: './task-batch.component.html',
  styleUrls: ['./task-batch.component.scss'],
})

export class TaskBatchComponent implements OnInit {
    // 页面是否已就绪
    pageReady = true;
    Math = Math;
    // 窗口是否显示
    windowVisible = true;
    // 表格配置
    tableConfig: TableConfig;
    // 列配置
    columnDefs: ColumnDef[];
    i18nLabel = this.taskBatchStr.i18nLabel;
    stepNameOpt: { [key: string]: string | number } = {};
    dataRest: Rest;
    // 传给模板使用
    taskStatus = Status;
    showErrorDetail = false;
    errorData: ErrorData;
    beforeTaskID: any;
    isStopPolling = false;
    resultTip: string;

    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    options: BaseRequestOptionsArgs;

    @Input() taskData: TaskData;
    @Input() taskName: string;
    @Input() customTip?: string;
    @Input() customTipType = 'info';
    @ViewChild('dt') table: DataTableComponent;
    @Output() onClose: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        public taskBatchStr: TaskBatchStrService,
        private service: TaskCenterService,
        private lazyLoadService: LazyloadService,
        private router: Router,
    ) { }

    ngOnInit(): void {
        this.resultTip = this.i18nLabel.resultBackgroudExecuteinfo;
        this.dataRest = this.service.getTaskStepsRest(this.taskData.id);
        this.initTable();
    }

    initTable(): void {
        const i18nLabel = this.i18nLabel;
        const taskName = this.taskName;

        this.tableConfig = {
            dataKey: 'id',
            pollingPeriod: 0.5,
            rowsPerPageOptions: null,
            pageStyle: 'simple',
            order: { serialNum: 'desc' },
            onPolling: (): void => {
                // 屏蔽activateContainer任务报错，影响场景为激活容器服务
                if(taskName === 'activateContainer') {
                    this.options = { doException: false };
                }

                // 子步骤表格刷新时，同步刷新一下任务信息
                this.updateTask(this.options);
            },
            beforeShow: (data): any[] => {
                if (!this.beforeTaskID || this.beforeTaskID !== this.taskData.id) {
                    this.stepNameOpt = {};
                    this.beforeTaskID = this.taskData.id;
                }

                data.forEach((item) => {
                    this.stepNameOpt[item.name] = item.displayName;
                });

                this.table.setFilterOptions('name', this.stepNameOpt);

                // 执行结束后(成功或失败)停止轮训
                if (this.taskData.taskStatus === this.taskStatus.success || this.taskData.taskStatus === this.taskStatus.failed) {
                    if (this.isStopPolling) {
                        this.table.stopPolling();
                    }
                    this.isStopPolling = true;
                }

                return data;
            },
        };
        this.columnDefs = [
            {
                header: i18nLabel.numberCol,
                field: 'serialNum',
                columnFilter: true,
                sortable: true,
                hidden: true,
            },
            {
                header: i18nLabel.stepName,
                field: 'name',
                filterType: 'dropdown',
                width: 200,
                options: this.stepNameOpt,
                render: (value, col, data: TaskData): string | number => data.displayName,
            },
            {
                field: 'id',
                header: 'ID',
                columnFilter: true,
                sortable: true,
                hidden: true,
            },
            {
                header: i18nLabel.executeTimes,
                field: 'executeTimes',
                columnFilter: true,
                render: (value, col, data): string | number => `${data.object.currentExecuteTimes}/${data.object.step_times}`,
            },
            {
                header: i18nLabel.object,
                field: 'object',
                columnFilter: true,
                render: (value): string | number => value.objectList && value.objectList.length && value.objectList[value.currentExecuteIndex] ?
                    value.objectList[value.currentExecuteIndex] :
                    UNPLACEHOLDER,
            },
            {
                header: i18nLabel.stepStatus,
                field: 'execStatus',
                filterType: 'dropdown',
                options: this.taskBatchStr.taskStatusOptions,
                columnFilter: true,
            },
            {
                header: i18nLabel.startTime,
                field: 'startTime',
                filterType: 'dateTimeRange',
                columnFilter: true,
                sortable: true,
                hidden: true,
                render: (value): string | number => this.taskBatchStr.renderTime(Number(value)),
            },
            {
                header: i18nLabel.endTime,
                field: 'endTime',
                filterType: 'dateTimeRange',
                columnFilter: true,
                sortable: true,
                hidden: true,
                render: (value): string | number => this.taskBatchStr.renderTime(Number(value)),
            },
            {
                header: i18nLabel.duration,
                field: 'duration',
                columnFilter: true,
                render: (value, col, data): string | number => {
                    const duration = this.taskBatchStr.getDuration(data.startTime, data.endTime);
                    return this.taskBatchStr.renderDuration(duration);
                },
            },
        ];
    }

    onShowErrorDetail(step: StepData): void {
        this.showErrorDetail = true;
        const { code, description, suggestion } = step.description;

        // 新详情模板description返的是数组
        const useNewTemplate = Array.isArray(description) && NEW_DETAIL_ERROR_NAME.includes(step.name);
        this.errorData = {
            name: useNewTemplate ?
                this.taskBatchStr.preCheckStepFailedTitle(step.displayName) : this.taskBatchStr.stepFailedTitle(step.displayName),
            code,
            description,
            suggestion,
            useNewTemplate,
        };
    }

    updateTask(options?: BaseRequestOptionsArgs): void {
        this.service.getTask(this.taskData.id, options).subscribe(({ data }) => {
            const { currentStepIndex, stepCount } = data.description;
            this.taskData = {
                ...data,
                progress: Math.floor(currentStepIndex / stepCount * 100),
            };
            if (this.taskData.taskStatus === this.taskStatus.success) {
                this.resultTip = this.i18nLabel.taskSucceed_tip;
            }
            if (this.taskData.taskStatus === this.taskStatus.failed) {
                this.resultTip = this.i18nLabel.taskFailed;
            }
        });
    }

    close(): void {
        this.onClose.emit();
        this.lazyLoadService.destroySelf(this);
    }

    /**
     * 跳转到任务中心
     */
    jumpToTask(): void {
        this.router.navigate(['/monitor/task']);
    }
}
